import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TabIndices } from '@/common/i-doc/i-doc.registry.ts';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { financeLegacyService, FinanceLegacyStats, FinanceLegacyStatsBreakdown, FinanceLegacyOptions } from '@services/finance-legacy.service.ts';
import { toastService } from '@services/toast.service.ts';

const INCOME_COLOR = '#2e7d32';
const EXPENSE_COLOR = '#d32f2f';
const SOURCE_APP_COLORS = ['#9e9e9e', '#1976d2']; // Легасі, Веб

// FIX: захист від неповної відповіді (напр. старий бекенд-процес ще не
// підхопив нову форму /stats) - краще показати 0, ніж впасти на toLocaleString.
const fmt = (n: number | undefined) => (n ?? 0).toLocaleString('uk-UA', { maximumFractionDigits: 2 });

// Зразкова агрегація по ВСЬОМУ масиву записів (не по поточній сторінці
// пагінації, як у вкладці "Записи"). FIX (2026-08-20): entries.type = 1
// розхід / 2 дохід - перша версія сумувала все разом, дохід і розхід
// змішувались в одну цифру. Тепер рахуються окремо + різниця (netUsd).
//
// FIX (2026-08-21, уточнення користувача): все на базі USD ("грн не
// цікава"). Додано розрізи по джерелах/легасі-веб/роках ("різноманітніша
// статистика", користувач прямо попросив) - byProject/bySource/
// bySourceApp/byYear, спільна таблична розмітка через BreakdownTable.
const BreakdownTable: React.FC<{
  title: string;
  rows: (FinanceLegacyStatsBreakdown & { label: string; key: string | number })[];
  groupLabel: string;
}> = ({ title, rows, groupLabel }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{groupLabel}</TableCell>
            <TableCell>Кількість</TableCell>
            <TableCell>Дохід (USD)</TableCell>
            <TableCell>Розхід (USD)</TableCell>
            <TableCell>Різниця (USD)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell>{row.label}</TableCell>
              <TableCell>{row.count}</TableCell>
              <TableCell sx={{ color: 'success.main' }}>{fmt(row.incomeUsd)}</TableCell>
              <TableCell sx={{ color: 'error.main' }}>{fmt(row.expenseUsd)}</TableCell>
              <TableCell sx={{ color: row.netUsd >= 0 ? 'success.main' : 'error.main' }}>{fmt(row.netUsd)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

// FIX (2026-08-21, за проханням користувача): фільтр "Джерело" - `sourceId`
// застосовується до всіх розрізів на беку (byProject/bySource/bySourceApp/byYear).
export const FinanceLegacyStatsTab: React.FC<{ options: FinanceLegacyOptions }> = ({ options }) => {
  const [stats, setStats] = useState<FinanceLegacyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    financeLegacyService.getStats(sourceFilter ? Number(sourceFilter) : undefined)
      .then(setStats)
      .catch(() => toastService.error('Не вдалося завантажити статистику.'))
      .finally(() => setLoading(false));
  }, [sourceFilter]);

  return (
    <Box data-i-doc={TabIndices.DEV_FINANCE_LEGACY_STATS}>
      <PageHint iDoc={TabIndices.DEV_FINANCE_LEGACY_STATS} />

      <PageToolbar
        storageKey="filters:dev-finance-legacy-stats"
        filters={[
          {
            id: 'source',
            type: 'select',
            label: 'Джерело',
            value: sourceFilter,
            onChange: setSourceFilter,
            options: [{ value: '', label: 'Всі' }, ...options.sources.map((s) => ({ value: String(s.value), label: s.label }))],
          },
        ]}
        onReset={() => setSourceFilter('')}
        showReset
      />

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : !stats ? (
        <Typography sx={{ mt: 4, textAlign: 'center' }}>Немає даних.</Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 4, mb: 3, mt: 1, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="overline" color="text.secondary">Всього записів</Typography>
              <Typography variant="h5">{stats.totalEntries}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Дохід (USD)</Typography>
              <Typography variant="h5" color="success.main">{fmt(stats.totalIncomeUsd)}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Розхід (USD)</Typography>
              <Typography variant="h5" color="error.main">{fmt(stats.totalExpenseUsd)}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Різниця (USD)</Typography>
              <Typography variant="h5" color={stats.netUsd >= 0 ? 'success.main' : 'error.main'}>
                {fmt(stats.netUsd)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Дохід/розхід по роках (USD)</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={(stats.byYear ?? []).map((r) => ({ year: r.year, 'Дохід': r.incomeUsd, 'Розхід': r.expenseUsd }))} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 12 }} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={50} />
                <Tooltip formatter={(value: number) => fmt(value)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Дохід" fill={INCOME_COLOR} radius={[3, 3, 0, 0]} />
                <Bar dataKey="Розхід" fill={EXPENSE_COLOR} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <BreakdownTable
            title="По роках"
            groupLabel="Рік"
            rows={(stats.byYear ?? []).map((r) => ({ ...r, key: r.year, label: String(r.year) }))}
          />

          <BreakdownTable
            title="По проєктах"
            groupLabel="Проєкт"
            rows={(stats.byProject ?? []).map((r) => ({ ...r, key: r.projectId ?? 'none', label: r.label }))}
          />

          <BreakdownTable
            title="По джерелах"
            groupLabel="Джерело"
            rows={(stats.bySource ?? []).map((r) => ({ ...r, key: r.sourceId ?? 'none', label: r.label }))}
          />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Легасі vs Веб (частка записів)</Typography>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={(stats.bySourceApp ?? []).map((r) => ({ name: r.label, value: r.count }))}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry: { name: string; value: number }) => `${entry.name}: ${entry.value}`}
                >
                  {(stats.bySourceApp ?? []).map((r, idx) => (
                    <Cell key={r.sourceApp} fill={SOURCE_APP_COLORS[idx % SOURCE_APP_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          <BreakdownTable
            title="Легасі vs Веб"
            groupLabel="Інтерфейс"
            rows={(stats.bySourceApp ?? []).map((r) => ({ ...r, key: r.sourceApp, label: r.label }))}
          />
        </>
      )}
    </Box>
  );
};
